import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Check,
  Zap,
  Crown,
  Building2,
  Loader2,
  CreditCard,
  AlertCircle,
  ArrowRight,
  Lock
} from 'lucide-react'
import { apiService } from '../../services/api'
import { Button } from '../ui/button'
import { cn } from '../../utils/cn'
import { toast } from 'react-hot-toast'
import { useBranding } from '../../context/BrandingContext'

const PLAN_META = {
  basic:      { icon: Zap,       color: 'text-blue-500',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' },
  pro:        { icon: Crown,     color: 'text-accent-600', bg: 'bg-accent-600/10', border: 'border-accent-600/30' },
  enterprise: { icon: Building2, color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
}
const STANDARD_ORDER = ['basic', 'pro', 'enterprise']
const getPlanMeta = (planId) => PLAN_META[planId] || { icon: Zap, color: 'text-accent-600', bg: 'bg-accent-600/10', border: 'border-accent-600/20' }

// ── Activating screen (post-Stripe redirect) ─────────────────────────────────
const ActivatingScreen = () => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-surface-50 dark:bg-dark-300">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center gap-6 text-center max-w-sm px-6"
    >
      <div className="w-16 h-16 rounded-full bg-accent-600/10 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-accent-600 animate-spin" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
          Activating your subscription
        </h2>
        <p className="text-neutral-500 dark:text-neutral-400">
          Payment received — setting up your account. This only takes a moment.
        </p>
      </div>
      <div className="flex items-center gap-2 text-sm text-neutral-400">
        <Lock className="w-4 h-4" />
        <span>Secured by Stripe</span>
      </div>
    </motion.div>
  </div>
)

// ── Plan card ─────────────────────────────────────────────────────────────────
const PlanCard = ({ planId, plan, isSelected, isHighlighted, onSelect }) => {
  const meta = getPlanMeta(planId)
  const Icon = meta.icon

  const featureList = [
    plan.features?.sites
      ? `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} website${plan.features.sites === 1 ? '' : 's'}`
      : null,
    plan.features?.editsPerMonth
      ? `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`
      : null,
    plan.features?.storage || null,
    plan.features?.support || null,
    plan.features?.analytics ? 'Analytics' : null,
    plan.features?.backup ? 'Automated backups' : null,
  ].filter(Boolean)

  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onSelect(planId)}
      className={cn(
        'relative w-full text-left rounded-2xl border-2 p-6 transition-all duration-200',
        isSelected
          ? 'border-accent-600 bg-white dark:bg-dark-200 shadow-colored'
          : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200 hover:border-accent-600/50',
        isHighlighted && !isSelected && 'border-accent-600/40'
      )}
    >
      {isHighlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-600 text-white">
            Most popular
          </span>
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className={cn('p-2 rounded-xl', meta.bg)}>
          <Icon className={cn('w-5 h-5', meta.color)} />
        </div>
        {isSelected && (
          <div className="w-5 h-5 rounded-full bg-accent-600 flex items-center justify-center flex-shrink-0">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      <div className="mb-4">
        <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
          {plan.name || planId.charAt(0).toUpperCase() + planId.slice(1)}
        </h3>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            £{plan.price}
          </span>
          <span className="text-neutral-500 dark:text-neutral-400 text-sm">/month</span>
        </div>
      </div>

      <ul className="space-y-2">
        {featureList.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Check className="w-3.5 h-3.5 text-accent-600 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
    </motion.button>
  )
}

// ── Main gate ─────────────────────────────────────────────────────────────────
const SubscriptionGate = ({ children }) => {
  const { cmsName } = useBranding()
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedPlan, setSelectedPlan] = useState(null)

  const isSuccessPage = location.pathname.endsWith('/billing/success')
  const sessionId = new URLSearchParams(location.search).get('session_id')
  const isPostPayment = isSuccessPage && !!sessionId

  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(r => r.data),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      refetchInterval: isPostPayment ? 3000 : false,
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { enabled: !profileLoading }
  )

  const billingStatus = profile?.billing_status
  const assignedPlanPrice = plansRaw?.[profile?.subscription_plan]?.price
  const isFreePlan = assignedPlanPrice === 0
  const isActive = billingStatus === 'active' || billingStatus === 'trialing' || isFreePlan

  // Pre-select the plan the admin assigned; fall back to 'pro' only if none assigned
  useEffect(() => {
    if (profile && !selectedPlan) {
      setSelectedPlan(profile.subscription_plan || 'pro')
    }
  }, [profile])

  const checkoutMutation = useMutation(
    (planId) => apiService.post('/billing/create-checkout-session', { planId }).then(r => r.data),
    {
      onSuccess: (data) => {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl
        } else {
          toast.error('No checkout URL returned. Please try again.')
        }
      },
      onError: () => toast.error('Failed to start checkout. Please try again.')
    }
  )

  // Once payment confirmed by webhook, go to dashboard
  useEffect(() => {
    if (isPostPayment && isActive) {
      navigate('/client', { replace: true })
    }
  }, [isActive, isPostPayment, navigate])

  // While checking, show children briefly (they'll be behind the gate when it appears)
  if (profileLoading) return <>{children}</>

  // Active subscription — pass through entirely
  if (isActive) return <>{children}</>

  // Just came back from Stripe — show activating screen while polling
  if (isPostPayment) return <ActivatingScreen />

  // Build plans array: standard plans in order, then any custom plans appended
  const plans = plansRaw
    ? [
        ...STANDARD_ORDER.filter(id => plansRaw[id]).map(id => ({ id, ...plansRaw[id] })),
        ...Object.entries(plansRaw)
          .filter(([id, p]) => !STANDARD_ORDER.includes(id) && p.custom)
          .map(([id, p]) => ({ id, ...p }))
      ]
    : []

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Blurred backdrop showing dashboard behind */}
      <div className="fixed inset-0 bg-dark-300/80 backdrop-blur-sm" />

      <div className="relative min-h-full flex flex-col items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-4xl"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-600/10 border border-accent-600/20 text-accent-600 text-sm font-medium mb-4">
              <CreditCard className="w-4 h-4" />
              Subscription required
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Activate your {cmsName} subscription
            </h1>
            <p className="text-neutral-400 max-w-md mx-auto">
              Choose a plan to unlock your dashboard and start managing your website.
            </p>
          </div>

          {/* Plan cards */}
          {plans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  planId={plan.id}
                  plan={plan}
                  isSelected={selectedPlan === plan.id}
                  isHighlighted={plan.id === profile?.subscription_plan}
                  onSelect={setSelectedPlan}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 mb-8">
              <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
            </div>
          )}

          {/* CTA */}
          <div className="flex flex-col items-center gap-3">
            <Button
              size="lg"
              onClick={() => checkoutMutation.mutate(selectedPlan)}
              disabled={!selectedPlan || checkoutMutation.isLoading || plans.length === 0}
              className="px-10 h-12 text-base font-semibold"
            >
              {checkoutMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  Get started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-neutral-500 flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              Payments secured by Stripe · Cancel anytime
            </p>
          </div>

          {/* Already paid notice */}
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-neutral-500">
            <AlertCircle className="w-4 h-4" />
            <span>Already paid? <button onClick={() => window.location.reload()} className="text-accent-600 hover:underline">Refresh this page</button></span>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default SubscriptionGate
